import type { ReactNode } from 'react';
import type { RedirectInfo } from '../handoff';
import { Tooltip } from './Tooltip';

// Highlighted phrase that reveals the address in a tooltip on hover/tap,
// keeping long URLs out of the banner copy on narrow screens.
function AddressTip({ url, children, className }: { url: string; children: ReactNode; className: string }) {
  return (
    <Tooltip label={url}>
      <button
        type="button"
        className={`cursor-help font-semibold underline decoration-dotted underline-offset-2 ${className}`}
      >
        {children}
      </button>
    </Tooltip>
  );
}

// Notice shown above the auth card when the sign-in was launched from a
// registered legacy host (see LEGACY_HOSTS in allowlist.ts). Blue/informational
// when the app has a registered new home (redirect is automatic); amber/caution
// when the legacy address has no successor yet.
export function LegacyBanner({ redirectInfo }: { redirectInfo: RedirectInfo }) {
  const moved = Boolean(redirectInfo.legacyBase);

  const style = moved
    ? {
        container: 'border-sf-blue-vibrant/40 bg-sf-blue-vibrant/10 dark:border-sf-blue-vibrant/30',
        icon: 'text-sf-blue-vibrant',
        title: 'text-sf-blue-dark dark:text-blue-200',
        body: 'text-sf-blue-dark/80 dark:text-blue-300',
        tip: 'text-sf-blue-vibrant dark:text-blue-200',
      }
    : {
        container: 'border-amber-300 bg-amber-50 dark:border-amber-400/30 dark:bg-amber-400/10',
        icon: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-700 dark:text-amber-300',
        body: 'text-amber-600 dark:text-amber-400',
        tip: 'text-amber-700 dark:text-amber-200',
      };

  return (
    <div className={`mb-4 rounded-xl border p-4 shadow-sm ${style.container}`}>
      <div className="flex items-center gap-2">
        <svg
          className={`h-4 w-4 shrink-0 ${style.icon}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          {moved ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          )}
        </svg>
        <p className={`text-sm font-semibold ${style.title}`}>
          {moved ? 'Heads up — this app has moved' : 'Legacy address'}
        </p>
      </div>
      <p className={`mt-1.5 text-left text-xs leading-relaxed sm:text-sm ${style.body}`}>
        {moved ? (
          <>
            You arrived via this app&apos;s{' '}
            <AddressTip url={redirectInfo.legacyBase!} className={style.tip}>
              legacy address
            </AddressTip>
            , which is being phased out. After signing in you&apos;ll be redirected to its{' '}
            <AddressTip url={redirectInfo.base} className={style.tip}>
              new home
            </AddressTip>{' '}
            — update your bookmarks.
          </>
        ) : (
          <>
            This app still uses a{' '}
            <AddressTip url={redirectInfo.base} className={style.tip}>
              legacy address
            </AddressTip>
            . Sign-in works as usual, but this address is being phased out — update your
            bookmarks once the app moves to its new home.
          </>
        )}
      </p>
    </div>
  );
}
